import React, { useEffect, useMemo, useContext, useState, Fragment } from "react";
import moment from "moment";
import cn from "classnames";
import { SettingsContext } from "../SettingsProvider";
import { itemsToBoardState, BoardState } from "../tools/events";
import { Board } from "../types";
import { loadSet, saveSet } from "../tools/storage";
import Statuses from "../common/Statuses";
import ItemsInNode from "../treeView/ItemsInNode";
import { funnel as getFunnel, Funnel, FunnelNode, FunnelInfo } from "../tools/funnel";
import FunnelCanvas from "./FunnelCanvas";
import { FunnelItem } from "../tools/treeFunnel";
import AttentionBox from "../common/AttentionBox";
import IconInfo from "../common/IconInfo";
import Tooltip, { Direction } from "../common/Tooltip";
import Icon from "../common/Icon";

import styles from "./FunnelView.module.scss";

interface Props {
    board: Board;
    initialTrackedLabels?: Set<string>;
}

const FunnelView: React.FC<Props> = function ({ board, initialTrackedLabels }: Props) {
    const { settings, context, labels } = useContext(SettingsContext);
    const { column } = settings;

    const [funnel, setFunnel] = useState<Funnel>();
    const [funnelInfo, setFunnelInfo] = useState<FunnelInfo>();
    const [boardState, setBoardState] = useState<BoardState>();
    const storeKey = useMemo(() => `trackedStatuses-${context.instanceId}-${column}`, [context.instanceId, column]);
    const [trackedLabels, setTrackedLabels] = useState<Set<string>>();

    const [selectedNode, setSelectedNode] = useState<FunnelNode>();
    const [selectedNodeItems, setSelectedNodeItems] = useState<FunnelItem[]>();

    const dark = context.theme === "dark";

    // load async setting from storage
    useEffect(() => {
        const f = async (): Promise<void> => {
            setTrackedLabels((await loadSet(storeKey)) || initialTrackedLabels || new Set([]));
        };
        f();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (board) {
            const now = moment();
            const boardState = itemsToBoardState(board, now);
            setBoardState(boardState);
        }
    }, [board]);

    useEffect(() => {
        const MAX_NODE_HEIGHT_RATIOS: { [setting: string]: number } = {
            "0": 1000,
            "1": 40,
            "2": 20,
            "3": 10,
            "4": 1
        };

        if (boardState && column && labels && trackedLabels) {
            const { funnel, info } = getFunnel(boardState, column, labels, trackedLabels, {
                centerVertically: true,
                weightColumnId: settings.numberColumn,
                maxNodeHeightRatio: settings.nodeHeightRescale
                    ? MAX_NODE_HEIGHT_RATIOS[settings.nodeHeightRescale]
                    : MAX_NODE_HEIGHT_RATIOS[0],
                calculateForIgnoredStatuses: false
            });

            setFunnel(funnel);
            setFunnelInfo(info);
            setSelectedNode(undefined);
            setSelectedNodeItems(undefined);
        }
    }, [boardState, column, labels, trackedLabels, storeKey, settings.numberColumn, settings.nodeHeightRescale]);

    const handleSetSelectedLabelIds = (selectedLabelIds: Set<string>): void => {
        setTrackedLabels(selectedLabelIds);
        setSelectedNode(undefined);
        saveSet(storeKey, selectedLabelIds);
    };

    const handleSelectAllLabelIds = (): void => {
        const allLabels = new Set(labels.orderedIds);
        setTrackedLabels(allLabels);
        setSelectedNode(undefined);
        saveSet(storeKey, allLabels);
    };

    const handleSelectNode = (node: FunnelNode | null): void => {
        if (!node || selectedNode === node) {
            setSelectedNode(undefined);
            setSelectedNodeItems(undefined);
        } else {
            setSelectedNode(node);
            const allItems = ([] as FunnelItem[]).concat(
                ...funnel!.slice(node.idx, funnel!.length).map((node) => node.items)
            );
            setSelectedNodeItems(allItems);
        }
    };

    if (funnel && boardState && trackedLabels) {
        return (
            <div className={cn({ [styles.dark]: dark })}>
                <Statuses selectedLabelIds={trackedLabels} setSelectedLabelIds={handleSetSelectedLabelIds} />

                {trackedLabels.size === 0 && (
                    <AttentionBox title="Choose statuses" inContainer>
                        <p>
                            Choose one or more statuses above to see funnel graph for them. (
                            <span className="link" onClick={handleSelectAllLabelIds}>
                                select all
                            </span>
                            )
                        </p>
                        <p>You can drag and drop stauses to change their order.</p>
                    </AttentionBox>
                )}
                {trackedLabels.size > 0 && (
                    <Fragment>
                        <div className={styles.filters}>
                            {funnelInfo?.rescaledNodeHeight && (
                                <div className={cn(styles.filter, styles.actionable, "tooltip")}>
                                    <Icon iconClass="icon__showall" />
                                    &nbsp;&nbsp;Nodes may be rescaled and not show actual weight ratio.&nbsp;
                                    <IconInfo />
                                    <Tooltip
                                        type="dark"
                                        wide
                                        delay
                                        direction={Direction.BOTTOM}
                                        text={`This is to prevent very high columns. Higher weight/count will still show as a higher column, but ratio is not accurate. See "Max height of columns" View Setting.`}
                                    />
                                </div>
                            )}
                            {funnelInfo?.zeroWeight && (
                                <div className={styles.filter}>All weights are 0, nothing to show.</div>
                            )}
                            {funnelInfo?.ignoredItems ? (
                                <div className={styles.filter}>
                                    Showing [All Items] as{" "}
                                    <span className={styles.strong}>{funnelInfo.ignoredItems}</span> items are not in
                                    selected statuses
                                </div>
                            ) : null}
                        </div>
                        {!funnelInfo?.zeroWeight && (
                            <Fragment>
                                <FunnelCanvas
                                    funnel={funnel}
                                    trackedLabels={trackedLabels}
                                    onSelectNode={handleSelectNode}
                                    selectedNode={selectedNode}
                                    showDeadEnds={settings.showDeadEnds}
                                />
                                <ItemsInNode
                                    items={selectedNodeItems}
                                    selectedLevel={
                                        selectedNode?.idx !== undefined
                                            ? selectedNode.idx - (funnelInfo?.ignoredItems ? 1 : 0)
                                            : undefined
                                    }
                                    board={board}
                                    boardState={boardState}
                                    trackedLabels={trackedLabels}
                                    weightColumnId={settings.numberColumn}
                                />
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </div>
        );
    } else {
        return <div>...</div>;
    }
};

export default FunnelView;
